import React, { useEffect } from 'react';
import propTypes from 'prop-types';
import axios from 'axios';
import Card from 'components/Card/Card';
import AgencyTreeFindBar from 'tailwind-components/AgencyTreeFindBar/AgencyTreeFindBar';
import useAgencyTreeFind from 'tailwind-components/AgencyTreeFindBar/useAgencyTreeFind';
import { isSingleAgencyWithoutDownline } from 'helpers/agencyManagement/agency-tree';
import CollapsibleListComponent, {
  AGENCY_TREE_NODE_ID_PREFIX
} from './CollapsibleListComponent';

/**
 * Sunshine's Agency Management page: the agencies tree plus a Cmd+F-style find bar
 * @param localLoader - whether the page loader is showing
 * @param agenciesTree - the agencies tree from redux
 * @param getAgenciesTree - action loading the tree
 * @param setLocalLoader - toggles the page loader
 */
export default function AgencyManagement({
  localLoader,
  agenciesTree,
  getAgenciesTree,
  setLocalLoader
}) {
  const cancelTokenSource = axios.CancelToken.source();
  useEffect(() => {
    getAgenciesTree(setLocalLoader, cancelTokenSource);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  const treeIsEmpty = agenciesTree.length === 1 && agenciesTree[0].length === 0;
  // a user whose only agency has no downline gets shortcut buttons, not a tree,
  // so there is nothing to find
  const hasTree = !treeIsEmpty && !isSingleAgencyWithoutDownline(agenciesTree);

  const find = useAgencyTreeFind({
    roots: agenciesTree,
    nodeIdPrefix: AGENCY_TREE_NODE_ID_PREFIX
  });

  return (
    <Card container fill title='Agency Management' loading={localLoader}>
      <span>
        View agencies hierarchy, agency details and update agency settings.
      </span>
      {treeIsEmpty ? (
        <>
          <br></br>
          <div>You have no agency assigned.</div>
        </>
      ) : (
        <>
          {hasTree && (
            <div className='mt-4 mb-2'>
              <AgencyTreeFindBar
                searchQuery={find.searchQuery}
                onSearchChange={find.setSearchQuery}
                onSearchKeyDown={find.handleSearchKeyDown}
                searchTrimmed={find.searchTrimmed}
                matchIndex={find.matchIndex}
                matchCount={find.matchCount}
                onPrevMatch={find.goPrevMatch}
                onNextMatch={find.goNextMatch}
              />
            </div>
          )}
          <CollapsibleListComponent data={agenciesTree} />
        </>
      )}
    </Card>
  );
}

AgencyManagement.propTypes = {
  localLoader: propTypes.bool,
  agenciesTree: propTypes.array,
  getAgenciesTree: propTypes.func,
  setLocalLoader: propTypes.func
};
